"use client";

import { useId, useState, type ReactNode } from "react";
import Link from "next/link";
import { CheckIcon } from "@/components/feature-icons";
import { InfoIcon, ArrowRightIcon } from "@/components/icons";
import { Glyph } from "@/app/pricing/comparison-icons";
import {
  type WorkedComparison,
  type Status,
  STATUS_LABEL,
  mirroredMatrix,
  ALT_CANVAS_SUMMARY,
  TAKEAWAY_KLEIP,
  TAKEAWAY_ALT,
  TAKEAWAY_EXCLUDES,
  COMMERCIAL_SUMMARY,
  SERVICE_SCOPE,
  ALT_HEADING,
  ALT_SUBHEADING,
  EST_MONTHLY_NOTE,
  KLEIP_PRICE_CONTEXT,
  KLEIP_PRICE_CONTEXT_KYC,
  TWELVE_MONTH_NOTE,
  DRAWER_DISCLAIMER,
} from "@/lib/pricing-comparison-data";

/* --------------------------------------------------------------- primitives */

function Label({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return (
    <p
      className={`text-label-sm font-semibold uppercase tracking-[0.14em] ${
        muted ? "text-on-surface-variant" : "text-brand-electric"
      }`}
    >
      {children}
    </p>
  );
}

function InfoDisclosure({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="inline">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={id}
        aria-label={label}
        className="ml-1 inline-flex translate-y-[2px] rounded-full text-on-surface-variant/80 transition-colors hover:text-brand-electric focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-electric"
      >
        <InfoIcon className="h-4 w-4" />
      </button>
      {open && (
        <span
          id={id}
          className="mt-2 block rounded-lg border border-outline-variant/70 bg-surface-container-low/70 p-3 text-[13px] leading-6 text-on-surface-variant"
        >
          {text}
        </span>
      )}
    </span>
  );
}

function IconTile({ name }: { name: string }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-container-high text-on-surface-variant">
      <Glyph name={name} className="h-5 w-5" />
    </span>
  );
}

/** Consistent, objective status vocabulary. No red / X. */
/** Just the status glyph — cyan check / neutral check / dot / dash. */
function StatusGlyph({ status }: { status: Status }) {
  if (status === "included" || status === "included-note") {
    return <CheckIcon className="h-4 w-4 shrink-0 text-brand-cyan" />;
  }
  if (status === "included-est") {
    return <CheckIcon className="h-4 w-4 shrink-0 text-outline" />;
  }
  if (status === "costed-est") {
    return (
      <span
        aria-hidden
        className="h-1.5 w-1.5 shrink-0 rounded-full bg-outline"
      />
    );
  }
  if (status === "not-in-est") {
    return <span aria-hidden className="h-px w-3 shrink-0 bg-outline" />;
  }
  return null;
}

/**
 * Status is the strongest element in a matrix cell. The glyph + label read
 * first; explanatory text sits beneath in smaller muted type.
 */
function StatusMark({ status, note }: { status: Status; note?: string }) {
  const strong =
    status === "included" ||
    status === "included-note" ||
    status === "usage";
  return (
    <>
      <span
        className={`flex items-center gap-1.5 text-[13px] font-semibold ${
          status === "not-in-est"
            ? "text-on-surface-variant/70"
            : strong
              ? "text-brand-navy"
              : "text-on-surface-variant"
        }`}
      >
        <StatusGlyph status={status} />
        {STATUS_LABEL[status]}
      </span>
      {note && (
        <span className="mt-0.5 block text-[11px] leading-4 text-on-surface-variant/70">
          {note}
        </span>
      )}
    </>
  );
}

/* ------------------------------------------------------------------- canvas */

function ComparisonCanvas({ data }: { data: WorkedComparison }) {
  return (
    <section className="rounded-2xl border border-outline-variant/60 bg-surface-container-low/25 p-4 sm:p-6">
      <div className="grid gap-6 md:grid-cols-[1fr_auto_1fr] md:gap-0">
        <div className="md:pr-7">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-electric">
            {data.planShort}
          </p>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-on-surface-variant/70">
            Bundled service
          </p>
          <p className="mt-3 text-[clamp(1.9rem,5.5vw,2.6rem)] font-bold leading-none text-brand-navy">
            {data.monthly}
          </p>
          <p className="mt-2 text-[13px] text-on-surface-variant">
            per month{" "}
            <span className="text-on-surface-variant/70">&middot; ex VAT</span>
          </p>
          <p aria-hidden className="mt-3 text-sm leading-none text-outline">
            &darr;
          </p>
          <p className="mt-1.5 text-[13px] leading-6 text-on-surface-variant">
            {KLEIP_PRICE_CONTEXT}
          </p>
          <p className="mt-1 text-xs font-medium leading-5 text-on-surface-variant/80">
            {KLEIP_PRICE_CONTEXT_KYC}
          </p>
        </div>

        <div className="relative flex items-center justify-center">
          <span
            aria-hidden
            className="absolute inset-x-0 top-1/2 h-px bg-outline-variant/60 md:inset-x-auto md:inset-y-0 md:left-1/2 md:h-auto md:w-px"
          />
          <span className="relative z-10 flex h-11 w-11 items-center justify-center rounded-full border border-outline-variant bg-surface-container-lowest text-xs font-bold uppercase tracking-wider text-on-surface-variant shadow-sm">
            vs
          </span>
        </div>

        <div className="md:pl-7">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
            {ALT_HEADING}
          </p>
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-on-surface-variant/70">
            {ALT_SUBHEADING}
          </p>
          <p className="mt-3 text-[clamp(1.9rem,5.5vw,2.6rem)] font-bold leading-none text-brand-navy">
            &asymp;&nbsp;{data.estMonthly}
          </p>
          <p className="mt-2 text-[13px] text-on-surface-variant">
            estimated monthly equivalent{" "}
            <span className="text-on-surface-variant/70">&middot; ex VAT</span>
            <InfoDisclosure
              label="How the estimate is derived"
              text={EST_MONTHLY_NOTE}
            />
          </p>
          <p aria-hidden className="mt-3 text-sm leading-none text-outline">
            &darr;
          </p>
          <ul className="mt-2 space-y-1.5">
            {ALT_CANVAS_SUMMARY.map((row) => (
              <li key={row.status} className="flex items-start gap-2">
                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                  <StatusGlyph status={row.status} />
                </span>
                <span className="text-[12px] leading-5">
                  <span
                    className={`font-semibold ${
                      row.status === "not-in-est"
                        ? "text-on-surface-variant/70"
                        : "text-brand-navy"
                    }`}
                  >
                    {STATUS_LABEL[row.status]}
                  </span>
                  <span className="text-on-surface-variant/70">
                    {" "}
                    &mdash; {row.items}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------- mirrored comparison */

function MirrorMatrix({ data }: { data: WorkedComparison }) {
  const rows = mirroredMatrix(data);
  return (
    <div>
      <Label>What&rsquo;s included in the price?</Label>
      <div className="mt-3 overflow-hidden rounded-xl border border-outline-variant/60">
        <div className="hidden bg-surface-container-low/60 md:grid md:grid-cols-[1.15fr_1fr_1.1fr] md:gap-4 md:px-4 md:py-2.5">
          {["Requirement", "KleipData", "Estimated alternative"].map((h, i) => (
            <span
              key={h}
              className={`text-label-sm font-semibold uppercase tracking-[0.12em] ${
                i === 1 ? "text-brand-navy" : "text-on-surface-variant"
              }`}
            >
              {h}
            </span>
          ))}
        </div>
        <ul className="divide-y divide-outline-variant/40">
          {rows.map((r) => (
            <li
              key={r.requirement}
              className="grid gap-1 px-4 py-2.5 md:grid-cols-[1.1fr_1fr_1fr] md:items-start md:gap-4"
            >
              <span className="flex items-center gap-2 text-[13px] font-medium text-brand-navy">
                <Glyph
                  name={r.icon}
                  className="h-4 w-4 shrink-0 text-on-surface-variant"
                />
                {r.requirement}
              </span>
              <span>
                <span className="mb-0.5 block text-[11px] font-semibold uppercase tracking-wide text-brand-electric md:hidden">
                  KleipData
                </span>
                <StatusMark
                  status={r.kleipdata.status}
                  note={r.kleipdata.note}
                />
              </span>
              <span>
                <span className="mb-0.5 mt-1 block text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant md:hidden">
                  Estimated alternative
                </span>
                <StatusMark
                  status={r.alternative.status}
                  note={r.alternative.note}
                />
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-2.5 text-xs leading-5 text-on-surface-variant/70">
        * Included with the KleipData platform. &ldquo;Not included in
        estimate&rdquo; means the cost is not in the displayed worked figure
        &mdash; not that an alternative provider cannot supply the capability.
      </p>
    </div>
  );
}

function TakeawayPanel({ data }: { data: WorkedComparison }) {
  return (
    <div className="rounded-2xl border border-outline-variant/60 bg-surface-container-low/25 p-5">
      <div className="grid gap-5 md:grid-cols-2 md:gap-8">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-electric">
            KleipData
          </p>
          <p className="mt-1 text-xl font-bold text-brand-navy">
            {data.monthly} / month
          </p>
          <p className="mt-2 text-[13px] leading-6 text-on-surface-variant">
            {TAKEAWAY_KLEIP}
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
            Estimated alternative
          </p>
          <p className="mt-1 text-xl font-bold text-brand-navy">
            &asymp; {data.estMonthly} / month equivalent
          </p>
          <p className="mt-2 text-[13px] leading-6 text-on-surface-variant">
            {TAKEAWAY_ALT}
          </p>
        </div>
      </div>
      <div className="mt-5 border-t border-outline-variant/50 pt-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
          Additional costs not included in the &asymp; {data.estMonthly} estimate
        </p>
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-medium text-brand-navy">
          {TAKEAWAY_EXCLUDES.map((x, i) => (
            <span key={x} className="flex items-center gap-2">
              {i > 0 && (
                <span aria-hidden className="text-outline">
                  &middot;
                </span>
              )}
              <span
                aria-hidden
                className="h-px w-3 shrink-0 bg-outline"
              />
              {x}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- tabs */

function OverviewTab({ data }: { data: WorkedComparison }) {
  return (
    <div className="space-y-10">
      <MirrorMatrix data={data} />
      <TakeawayPanel data={data} />
      <div>
        <Label>Commercial model</Label>
        <ul className="mt-3 divide-y divide-outline-variant/50">
          {COMMERCIAL_SUMMARY.map((row) => (
            <li
              key={row.label}
              className="grid gap-1 py-2.5 md:grid-cols-[1fr_1fr_1.3fr] md:items-center md:gap-4"
            >
              <span className="flex items-center gap-2.5 text-sm font-semibold text-brand-navy">
                <IconTile name={row.icon} />
                {row.label}
              </span>
              <span className="text-[13px] text-on-surface-variant">
                <span className="font-semibold text-brand-electric md:hidden">
                  KleipData:{" "}
                </span>
                {row.kleipdata}
              </span>
              <span className="text-[13px] text-on-surface-variant">
                <span className="font-semibold text-brand-navy md:hidden">
                  Alternative:{" "}
                </span>
                {row.alternative}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ScopeTab() {
  return (
    <div>
      <Label>Service scope</Label>
      <div className="mt-3 overflow-hidden rounded-xl border border-outline-variant/60">
        <div className="hidden bg-surface-container-low/60 md:grid md:grid-cols-[1.2fr_1fr_1.1fr] md:gap-4 md:px-4 md:py-2.5">
          {["Capability", "KleipData", "Estimated alternative"].map((h, i) => (
            <span
              key={h}
              className={`text-label-sm font-semibold uppercase tracking-[0.12em] ${
                i === 1 ? "text-brand-navy" : "text-on-surface-variant"
              }`}
            >
              {h}
            </span>
          ))}
        </div>
        <ul className="divide-y divide-outline-variant/40">
          {SERVICE_SCOPE.map((r) => (
            <li
              key={r.label}
              className="grid gap-1 px-4 py-2.5 md:grid-cols-[1.2fr_1fr_1fr] md:items-center md:gap-4"
            >
              <span className="flex items-center gap-2 text-[13px] font-medium text-brand-navy">
                <Glyph
                  name={r.icon}
                  className="h-4 w-4 shrink-0 text-on-surface-variant"
                />
                {r.label}
              </span>
              <span>
                <span className="mb-0.5 block text-[11px] font-semibold uppercase tracking-wide text-brand-electric md:hidden">
                  KleipData
                </span>
                <StatusMark status={r.kleipdata} />
              </span>
              <span>
                <span className="mb-0.5 mt-1 block text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant md:hidden">
                  Estimated alternative
                </span>
                <StatusMark status={r.alternative} />
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-2.5 text-xs leading-5 text-on-surface-variant/70">
        * Included with the KleipData platform.
      </p>
    </div>
  );
}

function CostTab({ data }: { data: WorkedComparison }) {
  return (
    <div className="space-y-8">
      <div>
        <Label>Estimated assembled alternative</Label>
        <p className="mt-2 text-2xl font-bold text-brand-navy">
          {data.annualMidpoint} / year
        </p>
        <p className="text-[13px] text-on-surface-variant">
          Midpoint of the worked range &middot;&nbsp;
          <span className="font-semibold text-brand-navy">
            &asymp; {data.estMonthly} / month
          </span>{" "}
          estimated equivalent &middot; ex VAT
        </p>
        <dl className="mt-3 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
          <div className="flex justify-between border-b border-outline-variant/50 py-1.5">
            <dt className="text-on-surface-variant">Worked range</dt>
            <dd className="font-medium text-brand-navy">
              {data.rangeLow}&ndash;{data.rangeHigh} / year
            </dd>
          </div>
          <div className="flex justify-between border-b border-outline-variant/50 py-1.5">
            <dt className="text-on-surface-variant">Equivalent range</dt>
            <dd className="font-medium text-brand-navy">
              {data.equivLow}&ndash;{data.equivHigh} / month
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <Label>KleipData 12-month equivalent</Label>
        <p className="mt-2 text-[15px] leading-7 text-on-surface-variant">
          {data.monthly} &times; 12 ={" "}
          <span className="text-xl font-bold text-brand-navy">
            {data.annualised}
          </span>{" "}
          ex VAT
        </p>
        <p className="mt-1 text-xs leading-6 text-on-surface-variant/70">
          {TWELVE_MONTH_NOTE}
        </p>
      </div>

      <div className="rounded-xl border border-outline-variant/70 bg-surface-container-low/40 p-4">
        <Label>Usage-based KYC / AML</Label>
        <p className="mt-2 text-[13px] leading-7 text-on-surface-variant">
          {data.kyc.callsPerYear} &middot; {data.kyc.calc} ={" "}
          <span className="font-semibold text-brand-navy">
            {data.kyc.annual} / year
          </span>{" "}
          ex VAT &middot; average{" "}
          <span className="font-semibold text-brand-navy">
            &asymp; {data.kyc.monthlyAvg} / month
          </span>
        </p>
        <p className="mt-2 text-xs leading-6 text-on-surface-variant/70">
          KYC / AML is usage based. The monthly average is illustrative, not a
          fixed charge, and is separate from the unlimited included intelligence
          services.
        </p>
      </div>

      <p className="border-t border-outline-variant/60 pt-4 text-xs leading-6 text-on-surface-variant/70">
        {DRAWER_DISCLAIMER}
      </p>
    </div>
  );
}

function AssumptionsTab({ data }: { data: WorkedComparison }) {
  return (
    <div className="space-y-6">
      <div>
        <Label>Worked scenario</Label>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {data.assumptions.map((a) => (
            <div
              key={a.label}
              className="flex items-center gap-2.5 rounded-xl border border-outline-variant/60 p-3 text-[13px]"
            >
              <IconTile name={a.icon} />
              <span className="text-on-surface-variant">{a.label}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-on-surface-variant/70">
          Prices compared ex VAT where applicable.
        </p>
      </div>

      <div>
        <Link
          href="/pricing/methodology"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-electric hover:underline"
        >
          <Glyph name="book" className="h-4 w-4" />
          View methodology &amp; sources
          <ArrowRightIcon className="h-4 w-4" />
        </Link>
        <p className="mt-1.5 text-xs leading-6 text-on-surface-variant/80">
          Assumptions, public pricing sources, review dates and calculation
          detail. Source publication is being finalised.
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- component */

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "scope", label: "Service scope" },
  { id: "cost", label: "Cost breakdown" },
  { id: "assumptions", label: "Assumptions" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function ComparisonContent({ data }: { data: WorkedComparison }) {
  const [tab, setTab] = useState<TabId>("overview");
  const tabId = useId();

  return (
    <div className="space-y-9">
      <ComparisonCanvas data={data} />

      <div>
        <div
          role="tablist"
          aria-label="Comparison detail"
          className="flex gap-1 rounded-xl bg-surface-container-high p-1 text-[13px]"
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`${tabId}-${t.id}`}
                aria-selected={active}
                aria-controls={`${tabId}-${t.id}-panel`}
                onClick={() => setTab(t.id)}
                className={`flex-1 rounded-lg px-2.5 py-2 font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric ${
                  active
                    ? "bg-surface-container-lowest text-brand-navy shadow-sm"
                    : "text-on-surface-variant hover:text-brand-navy"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div
          role="tabpanel"
          id={`${tabId}-${tab}-panel`}
          aria-labelledby={`${tabId}-${tab}`}
          className="mt-7"
        >
          {tab === "overview" && <OverviewTab data={data} />}
          {tab === "scope" && <ScopeTab />}
          {tab === "cost" && <CostTab data={data} />}
          {tab === "assumptions" && <AssumptionsTab data={data} />}
        </div>
      </div>
    </div>
  );
}
